/**
 * ─── HRIS Hooks ──────────────────────────────────────────────
 * Lifecycle hooks for HRIS entities.
 */

import type { EntityHooks, HookContext, EntityId } from '@repo/core'
import type { Employee } from './hris.schema'

export const HrisHooks: EntityHooks<Employee> = {
  beforeCreate: async (input: Record<string, unknown>, ctx: HookContext) => {
    // Normalize names
    if (typeof input.firstName === 'string') input.firstName = input.firstName.trim()
    if (typeof input.lastName === 'string') input.lastName = input.lastName.trim()
    if (typeof input.middleName === 'string') input.middleName = input.middleName.trim()

    // Compute fullName if not provided
    if (!input.fullName && input.firstName && input.lastName) {
      const middle = input.middleName ? ` ${input.middleName} ` : ' '
      const suffix = input.suffix ? `, ${input.suffix}` : ''
      input.fullName = `${input.firstName}${middle}${input.lastName}${suffix}`
    }

    // Auto-assign Pag-IBIG contribution based on salary threshold
    if (typeof input.basicSalary === 'number' && !input.pagibigContribution) {
      input.pagibigContribution = input.basicSalary > 1500 ? 200 : 100
    }

    input.tenantId = ctx.tenantId
    input.createdBy = ctx.userId

    return input
  },

  afterCreate: async (entity: Employee, ctx: HookContext) => {
    console.log(`[HRIS] Employee ${entity.employeeCode} — "${entity.fullName}" created by ${ctx.userId}`)
  },

  beforeUpdate: async (_id: EntityId, input: Record<string, unknown>, _ctx: HookContext) => {
    if (typeof input.firstName === 'string') input.firstName = input.firstName.trim()
    if (typeof input.lastName === 'string') input.lastName = input.lastName.trim()
    return input
  },

  afterUpdate: async (entity: Employee, ctx: HookContext) => {
    console.log(`[HRIS] Employee ${entity.employeeCode} updated by ${ctx.userId}`)
  },

  beforeDelete: async (_id: EntityId, _ctx: HookContext) => {
    // Employees should NOT be deleted — they should be marked as resigned/terminated
    // But we allow soft-delete for data correction purposes
  },

  afterDelete: async (entity: Employee, ctx: HookContext) => {
    console.log(`[HRIS] Employee ${entity.employeeCode} soft-deleted by ${ctx.userId}`)
  },

  beforeRead: async (_id: EntityId, _ctx: HookContext) => {
    // No special read logic
  },

  afterRead: async (entity: Employee | null, _ctx: HookContext) => {
    return entity
  },
}
